import { VERSION } from '@angular/core'

import { BugfenderClass } from '@bugfender/sdk'

import { environment } from '@env'

import { guardBugfenderProcessor } from '@utils/bugfender-processor-guard'
import { CrashReporter } from '@utils/crash-reporter'

/** The part of the Bugfender SDK the page starts with. */
export type BugfenderBootSdk = Pick<BugfenderClass, 'init'>
export type BugfenderOptions = Parameters<BugfenderClass['init']>[0]
/** The part of the crash reporter the window listeners use. */
export type WindowCrashReporter = Pick<CrashReporter, 'reportWindowError' | 'reportRejection'>

export const BUGFENDER_OPTIONS: BugfenderOptions = {
	appKey: environment.logging.loggingConfig.appKey,
	// The Angular the bundle runs on, so logs split before and after each upgrade. No commit id is available at
	// build time (cicd/buildspec.yml), and importing package.json would ship it whole to the browser.
	version: VERSION.full,
	build: VERSION.full,
	logUIEvents: false,
	// The page's own listeners report window errors instead (see `bootBugfender`).
	registerErrorHandler: false,
}

/**
 * Starts Bugfender before the rest of the app is evaluated, and listens for window errors for the whole life of the
 * page, so a failure while the page starts is reported too.
 *
 * The listeners replace the SDK's own (`registerErrorHandler: false`): those only start once its asynchronous init
 * has finished, cannot be bounded and cannot be removed. Ours send the same crash text the SDK 2.4.0 sends, so
 * existing crash groups keep matching, through the crash reporter's storm guard. A crash sent before init finishes
 * is queued by the SDK and sent once it is ready.
 */
export const bootBugfender = (
	sdk: BugfenderBootSdk,
	target: EventTarget,
	options: BugfenderOptions,
	reporter: WindowCrashReporter
): void => {
	// Reporting a failure must not raise another one, which would come back here as one more window error.
	const reportError = (event: Event): void => {
		try {
			reporter.reportWindowError(event as ErrorEvent)
		} catch {
			return
		}
	}

	const reportRejection = (event: Event): void => {
		try {
			reporter.reportRejection((event as PromiseRejectionEvent).reason)
		} catch {
			return
		}
	}

	target.addEventListener('error', reportError)
	target.addEventListener('unhandledrejection', reportRejection)
	guardBugfenderProcessor(sdk)
	sdk.init(options)
}
